/**
 * FRIDGE-1582 (FRIDGE-1508 K12) — keyboard focus inside a horizontal row
 * lands where the reader can see the whole focused element.
 *
 * Chrome leaves a focused element that is partly visible in a horizontal
 * scroller where it is, and on a row with scroll snapping it re-snaps after
 * its own focus scroll. Read on production 10/16/2026 (US egress), Tab through
 * every homepage rail (`.hp26 .rail`, scroll-snap-type x proximity, cards
 * snapping at their start): 6 of 335 stops at 1280 and 22 of 335 at 375 stayed
 * partly outside the rail, for example a 132 px category tile at 1194 to
 * 1326 px in a rail that ends at 1248 px, the rail parked on a snap point. On a
 * product page at 375 the quick-comparison table (`#quick-compare`, no snap)
 * left 3 of 7 focused links partly outside without scrolling at all.
 *
 * `revealShift` is the pure part: the horizontal scroll that brings the
 * element fully inside the scroller's visible box, choosing among the row's
 * own snap points when it snaps (so the browser does not snap it back out),
 * and the smallest move otherwise. `revealInScroller` reads the DOM and
 * scrolls; app/keyboard-focus-in-view.tsx runs it for keyboard focus only.
 */

export interface Span { left: number; right: number }

/** Within this many px counts as inside: layout and rounding noise. */
const TOLERANCE = 1

/**
 * The scroll shift (added to scrollLeft) that brings `el` fully inside
 * `port`, or 0 when it already is. With `snapShifts` (the shift that parks
 * each snap point), the smallest one under which `el` fits is chosen; when
 * none fits, or the row does not snap, the smallest move that shows `el`
 * whole. An element wider than the port is aligned to the port's start.
 */
export function revealShift(port: Span, el: Span, snapShifts: number[] | null = null): number {
  const fits = (d: number) => el.left - d >= port.left - TOLERANCE && el.right - d <= port.right + TOLERANCE
  if (fits(0)) return 0
  if (snapShifts && snapShifts.length) {
    const valid = snapShifts.filter(fits)
    if (valid.length) return valid.reduce((best, d) => (Math.abs(d) < Math.abs(best) ? d : best))
  }
  if (el.right - el.left > port.right - port.left) return Math.floor(el.left - port.left)
  // Round away from zero so a fractional position cannot leave a pixel outside.
  return el.left < port.left ? Math.floor(el.left - port.left) : Math.ceil(el.right - port.right)
}

/** The inline-axis value of a computed `scroll-snap-align` ("start", "center", "end" or "none"). */
function inlineAlign(value: string): string {
  const parts = value.trim().split(/\s+/)
  return parts[parts.length - 1] || 'none'
}

/** The nearest ancestor that the reader can scroll sideways and that has somewhere to scroll. */
export function horizontalScroller(el: Element): HTMLElement | null {
  for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const overflowX = getComputedStyle(node).overflowX
    if ((overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth + TOLERANCE) return node
  }
  return null
}

/** Scroll `el`'s nearest horizontal scroller so `el` is fully inside its visible box. Returns the shift applied. */
export function revealInScroller(el: Element): number {
  const scroller = horizontalScroller(el)
  if (!scroller) return 0
  const box = scroller.getBoundingClientRect()
  const left = box.left + scroller.clientLeft
  const port = { left, right: left + scroller.clientWidth }
  const r = el.getBoundingClientRect()
  const style = getComputedStyle(scroller)
  let snapShifts: number[] | null = null
  if (/\b(x|both|inline)\b/.test(style.scrollSnapType)) {
    const padStart = parseFloat(style.scrollPaddingLeft) || 0
    const padEnd = parseFloat(style.scrollPaddingRight) || 0
    snapShifts = []
    for (const child of Array.from(scroller.children)) {
      const align = inlineAlign(getComputedStyle(child).scrollSnapAlign)
      const c = child.getBoundingClientRect()
      if (align === 'start') snapShifts.push(c.left - (port.left + padStart))
      else if (align === 'end') snapShifts.push(c.right - (port.right - padEnd))
      else if (align === 'center') snapShifts.push((c.left + c.right) / 2 - (port.left + padStart + port.right - padEnd) / 2)
    }
  }
  const shift = revealShift(port, { left: r.left, right: r.right }, snapShifts)
  if (shift !== 0) scroller.scrollBy({ left: shift, behavior: 'instant' })
  return shift
}
